import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Users, Key, TrendingUp, Award, BookOpen } from 'lucide-react';
import { toast } from 'sonner';

export const InstitutionAdminDashboard = () => {
  const { user } = useAuth();
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [progress, setProgress] = useState([]);
  const [classes, setClasses] = useState([]);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usersRes, classesRes] = await Promise.all([
        api.get('/users', {
          params: { institution_id: user.institution_id }
        }),
        api.get('/classes'),
      ]);
      
      const allUsers = usersRes.data;
      setClasses(classesRes.data);
      setStudents(allUsers.filter(u => u.role === 'student'));
      setTeachers(allUsers.filter(u => u.role === 'teacher'));

      // Load progress for students
      const progressData = [];
      for (const student of allUsers.filter(u => u.role === 'student').slice(0, 10)) {
        try {
          const res = await api.get('/analytics/student-progress', {
            params: { student_id: student.id }
          });
          progressData.push({
            student,
            ...res.data
          });
        } catch (error) {
          console.error('Error loading progress for', student.email, error);
        }
      }
      setProgress(progressData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser || !newPassword) return;
    try {
      const formData = new FormData();
      formData.append('new_password', newPassword);
      await api.put(`/users/${selectedUser.id}/reset-password`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Password reset successfully');
      setShowPasswordDialog(false);
      setSelectedUser(null);
      setNewPassword('');
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  if (loading) {
    return <Layout><div className="text-center py-12">Loading school data...</div></Layout>;
  }

  const avgScore = progress.length > 0
    ? progress.reduce((sum, p) => {
        const attempts = p.quiz_attempts || [];
        if (attempts.length === 0) return sum;
        const studentAvg = attempts.reduce((s, a) => s + a.score, 0) / attempts.length;
        return sum + studentAvg;
      }, 0) / progress.length
    : 0;

  const totalQuizzes = progress.reduce((sum, p) => sum + (p.quiz_attempts?.length || 0), 0);
  const totalViews = progress.reduce((sum, p) => sum + (p.events?.length || 0), 0);

  return (
    <Layout title="Institution Admin Dashboard">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-indigo-900">School Dashboard</h1>
            <p className="text-slate-600 text-sm sm:text-base">Manage your school's users and view progress</p>
          </div>
        </div>

        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Users className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-bold text-amber-900 mb-1 text-sm">School-Level Access</h3>
                <p className="text-xs text-amber-800">You can reset passwords and view analytics for students and teachers in your school only. User creation is managed by Super Admin.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className="bg-white border-slate-200">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-2xl sm:text-3xl font-bold text-indigo-600">{students.length}</p>
                <p className="text-xs sm:text-sm text-slate-600 mt-1">Students</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-2xl sm:text-3xl font-bold text-cyan-600">{teachers.length}</p>
                <p className="text-xs sm:text-sm text-slate-600 mt-1">Teachers</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-2xl sm:text-3xl font-bold text-amber-600">{Math.round(avgScore)}%</p>
                <p className="text-xs sm:text-sm text-slate-600 mt-1">Avg Score</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-2xl sm:text-3xl font-bold text-green-600">{totalQuizzes}</p>
                <p className="text-xs sm:text-sm text-slate-600 mt-1">Quiz Attempts</p>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle className="text-indigo-900">Students</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left p-3 text-xs sm:text-sm font-medium text-slate-700">Name</th>
                    <th className="text-left p-3 text-xs sm:text-sm font-medium text-slate-700">Email</th>
                    <th className="text-left p-3 text-xs sm:text-sm font-medium text-slate-700">Class</th>
                    <th className="text-left p-3 text-xs sm:text-sm font-medium text-slate-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((student) => (
                    <tr key={student.id} className="border-b border-slate-100">
                      <td className="p-3 text-xs sm:text-sm font-medium">{student.name}</td>
                      <td className="p-3 text-xs sm:text-sm text-slate-600">{student.email}</td>
                      <td className="p-3 text-xs sm:text-sm text-slate-600">{classes.find(c => c.id === student.class_id)?.name || student.class_id || '-'}</td>
                      <td className="p-3">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedUser(student);
                            setShowPasswordDialog(true);
                          }}
                          className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 p-1 h-auto"
                        >
                          <Key className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle className="text-indigo-900">Teachers</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left p-3 text-xs sm:text-sm font-medium text-slate-700">Name</th>
                    <th className="text-left p-3 text-xs sm:text-sm font-medium text-slate-700">Email</th>
                    <th className="text-left p-3 text-xs sm:text-sm font-medium text-slate-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {teachers.map((teacher) => (
                    <tr key={teacher.id} className="border-b border-slate-100">
                      <td className="p-3 text-xs sm:text-sm font-medium">{teacher.name}</td>
                      <td className="p-3 text-xs sm:text-sm text-slate-600">{teacher.email}</td>
                      <td className="p-3">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedUser(teacher);
                            setShowPasswordDialog(true);
                          }}
                          className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 p-1 h-auto"
                        >
                          <Key className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle className="text-indigo-900">Student Progress</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {progress.map((p) => {
                const quizAttempts = p.quiz_attempts || [];
                const avgScore = quizAttempts.length > 0
                  ? quizAttempts.reduce((sum, a) => sum + a.score, 0) / quizAttempts.length
                  : 0;
                return (
                  <div key={p.student.id} className="p-4 border border-slate-200 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-bold text-indigo-900">{p.student.name}</h3>
                      <span className="text-sm font-bold text-amber-600">{Math.round(avgScore)}%</span>
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-lg font-bold text-cyan-600">{quizAttempts.length}</p>
                        <p className="text-xs text-slate-600">Quizzes</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-indigo-600">{p.events?.length || 0}</p>
                        <p className="text-xs text-slate-600">Views</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-green-600">{quizAttempts.filter(a => a.first_attempt).length}</p>
                        <p className="text-xs text-slate-600">First Attempts</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Reset Password</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">User</label>
                <Input value={selectedUser?.name || ''} disabled className="bg-slate-100" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">New Password</label>
                <Input 
                  type="password" 
                  value={newPassword} 
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  className="border-slate-300"
                />
              </div>
              <Button 
                onClick={handleResetPassword} 
                className="w-full bg-indigo-600 hover:bg-indigo-700"
                disabled={!newPassword}
              >
                Reset Password
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};